import { Component, Inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import {
  AddAdditionalImageDialogComponent,
  AdditionalImageDialogInputData,
  getPrimaryImageOrFirst,
  isMediaImage,
  PropertyMediaInfo,
} from '@miq/utils';
import { L10nTranslationService } from 'angular-l10n';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { ReportRecord, ReportView } from '../../../../models/report.model';
import { ReportAllRecordsImage, TemplateField } from '../../../../models/template.model';
import { ReportExportViewService } from '../../../../report-export-view.service';
import { REPORT_EXPORT_CONFIGS_TOKEN, ReportExportConfigurations } from '../../../../report-export-view.token';
import { ReportImagesService } from '../../../../report-images.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import {
  AdditionalImagesPosition,
  ADDITIONAL_IMAGE_FIELD_ID,
  IMAGE_FIELD_ID,
} from '../../../../shared/templates/config/common';

@UntilDestroy()
@Component({
  selector: 'ppt-record-image',
  templateUrl: './record-image.component.html',
  styleUrls: ['./record-image.component.scss'],
  standalone: false,
})
export class RecordImageComponent implements OnInit, OnChanges, OnDestroy {
  @Input() reportRecordData: ReportRecord;
  @Input() allFields: TemplateField[];
  @Input() currentView: ReportView;
  @Input() reportId: number;

  public selectedImageId: number;
  public imageField: TemplateField;
  public additionalImageField: TemplateField;
  public edpMultimediaURI: string;
  public s3BucketImagesUrl: string;
  public hasAdditionalImage = false;
  public reportAllImages: ReportAllRecordsImage;

  constructor(
    @Inject(REPORT_EXPORT_CONFIGS_TOKEN) reportModuleConfigs: ReportExportConfigurations,
    private notificationService: NotificationService,
    private reportExportViewService: ReportExportViewService,
    private reportImagesService: ReportImagesService,
    public translationService: L10nTranslationService
  ) {
    this.edpMultimediaURI = reportModuleConfigs.edpMultimediaURI;
    this.s3BucketImagesUrl = reportModuleConfigs.s3BucketImagesUrl;
  }

  ngOnChanges() {
    this.imageField = (this.allFields || []).find(field => field.id === IMAGE_FIELD_ID);
    this.additionalImageField = (this.allFields || []).find(field => field.id === ADDITIONAL_IMAGE_FIELD_ID);
    this.showAdditionalImage();
  }

  ngOnInit() {
    this.reportImagesService.onUpdateAdditionalImage$.pipe(untilDestroyed(this)).subscribe(response => {
      this.reportAllImages = response;
      this.reportRecordData = { ...this.reportRecordData };
      this.showAdditionalImage();
    });
  }

  ngOnDestroy() {
    // used for destroy the Subscription.
  }

  /**
   * This method is for check the Additional Image to the record under the report and assign selectedImageId if it have.
   */
  public showAdditionalImage() {
    let edpIdInfo = this.reportRecordData.associatedProperty
      ? this.reportRecordData.associatedProperty
      : this.reportRecordData.data;
    if (this.additionalImageField) {
      if (!!this.reportAllImages[edpIdInfo.entity.id]) {
        this.hasAdditionalImage = !!this.reportAllImages[edpIdInfo.entity.id];
        this.selectedImageId = this.hasAdditionalImage
          ? this.reportAllImages[edpIdInfo.entity.id][AdditionalImagesPosition.AdditionalImage].imageId
          : null;
      } else {
        const mediaData = (edpIdInfo['property_media_information'] || []).filter(media => !media.primary_image_f);
        if (mediaData && mediaData.length) {
          let sortedMedia = ((Array.isArray(mediaData) && mediaData) || []).sort(
            (imageA, imageB) => imageA.display_order - imageB.display_order
          );
          this.hasAdditionalImage = sortedMedia ? !!sortedMedia[0] : false;
          this.selectedImageId = sortedMedia ? sortedMedia[0].id : null;
        } else {
          this.hasAdditionalImage = false;
          this.selectedImageId = null;
        }
      }
    }
  }

  /**
   * method to add the additional image to the record under report only for China templates.
   */
  public addAdditionalImage(_method: 'add' | 'edit') {
    const imagesToDisplay: PropertyMediaInfo[] = this.getImagesWithoutPrimary();
    const options = { height: '80%', width: '80%', position: { top: '30px' } };
    const translatedData = {
      close: this.translationService.translate('core.close'),
      selectAttachment: this.translationService.translate('core.selectAttachment'),
      select: this.translationService.translate('recordsClipboard.select'),
      noImagesFound: this.translationService.translate('core.noImagesFound'),
    };
    const data: AdditionalImageDialogInputData = {
      imagesToDisplay,
      multiSelect: false,
      edpMultimediaURI: this.edpMultimediaURI,
      s3BucketImagesUrl: this.s3BucketImagesUrl,
      selectedImageId: this.selectedImageId,
      translatedData,
    };
    let selectedImage: PropertyMediaInfo;
    let edpIdInfo = this.reportRecordData.associatedProperty
      ? this.reportRecordData.associatedProperty
      : this.reportRecordData.data;
    this.notificationService
      .openDialog<PropertyMediaInfo>(AddAdditionalImageDialogComponent, data, options)
      .pipe(
        filter(image => image != null),
        switchMap((image: PropertyMediaInfo) => {
          selectedImage = image;
          let addEdit$: Observable<any> = of();

          if (Object.keys(this.reportAllImages).length == 0 || !this.reportAllImages[edpIdInfo.entity.id]) {
            addEdit$ = this.reportExportViewService.addAdditionalImage(this.reportId, [
              { edpRecordId: edpIdInfo.entity.id, imageId: image.id },
            ]);
          } else {
            const reportImageId =
              this.reportAllImages[edpIdInfo.entity.id][AdditionalImagesPosition.AdditionalImage].reportImageId;
            addEdit$ = this.reportExportViewService.changeAdditionalImage(this.reportId, reportImageId, image.id);
          }
          return addEdit$;
        })
      )
      .subscribe((repImageId: number | number[]) => {
        const reportImageId = Array.isArray(repImageId) ? repImageId[0] : repImageId;
        if (!this.reportAllImages[edpIdInfo.entity.id]) {
          this.reportAllImages[edpIdInfo.entity.id] = [
            {
              reportImageId,
              imageId: selectedImage.id,
            },
          ];
        } else {
          this.reportAllImages[edpIdInfo.entity.id][0] = {
            reportImageId,
            imageId: selectedImage.id,
          };
        }
        this.reportImagesService.setReportRecordImages(this.reportAllImages);
      });
  }

  /**
   * method for deleting the additional image for the record under the Report.
   */
  public deleteAdditionalImage() {
    let edpIdInfo = this.reportRecordData.associatedProperty
      ? this.reportRecordData.associatedProperty
      : this.reportRecordData.data;
    if (
      this.reportAllImages &&
      Object.keys(this.reportAllImages).length > 0 &&
      this.reportAllImages[edpIdInfo.entity.id]
    ) {
      const reportImageId = this.reportAllImages[edpIdInfo.entity.id][0].reportImageId;
      this.reportExportViewService.deleteAdditionalImage(this.reportId, reportImageId).subscribe(() => {
        delete this.reportAllImages[edpIdInfo.entity.id];
        this.reportImagesService.setReportRecordImages(this.reportAllImages);
        this.hasAdditionalImage = false;
        this.selectedImageId = null;
      });
    } else {
      this.hasAdditionalImage = false;
      this.selectedImageId = null;
    }
  }

  /**
   * this method is used to fetch the images without the primary image.
   */
  private getImagesWithoutPrimary() {
    let imagesToDisplay: PropertyMediaInfo[] = [];
    const mediaInfo = this.reportRecordData.associatedProperty
      ? this.reportRecordData.associatedProperty.property_media_information
      : this.reportRecordData.data.property_media_information;
    if (mediaInfo.length) {
      const primaryImage = getPrimaryImageOrFirst(mediaInfo);
      if (primaryImage) {
        imagesToDisplay = mediaInfo.filter(
          (media: PropertyMediaInfo) => media.id !== primaryImage.id && isMediaImage(media)
        );
      }
    }
    return imagesToDisplay;
  }
}
